import React, { useEffect, useState } from 'react';
import { clearAuthSession, checkAndFixSession } from '../lib/supabaseClient';
import { logger } from '../utils/logger';

interface SessionErrorHandlerProps {
  children: React.ReactNode;
}

const SessionErrorHandler: React.FC<SessionErrorHandlerProps> = ({ children }) => {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isFixing, setIsFixing] = useState(false);

  useEffect(() => {
    // Escuchar errores de consola relacionados con Supabase
    // Nota: Interceptamos console.error para detectar errores específicos de Supabase
    // pero aún necesitamos usar console.error directamente aquí para la interceptación
    const originalError = console.error;
    console.error = (...args) => {
      const errorMessage = args
        .map((arg) => {
          if (typeof arg === 'string') return arg;
          if (arg instanceof Error) return arg.message;
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        })
        .join(' ');
      
      // Detectar errores de clock skew o sesión futura
      if (errorMessage.includes('Session as retrieved from URL was issued in the future') ||
          errorMessage.includes('clock for skew') ||
          errorMessage.includes('future')) {
        setShowErrorModal(true);
      }
      
      // Ignorar errores de cache no críticos (ERR_CACHE_OPERATION_NOT_SUPPORTED)
      // Estos son errores del navegador que no afectan la funcionalidad
      if (errorMessage.includes('ERR_CACHE_OPERATION_NOT_SUPPORTED') ||
          errorMessage.includes('res.cloudinary.com')) {
        // Silently ignore - this is a browser cache warning for Cloudinary videos, not a critical error
        return;
      }

      // Ignorar "window.connect no disponible" - CCP/Amazon Connect no cargado (solo relevante para admin/agent)
      if (errorMessage.includes('window.connect no disponible')) {
        return;
      }

      // Ignorar errores de CCP iframe bloqueado o postMessage (connect-streams)
      if (
        errorMessage.includes("Cannot read properties of null (reading 'postMessage')") ||
        (errorMessage.includes('postMessage') && errorMessage.includes('null'))
      ) {
        return;
      }
      if (
        errorMessage.includes('frame-ancestors') ||
        errorMessage.includes('Error while checking for, and recreating, the CCP IFrame') ||
        errorMessage.includes('iframe_retries_exhausted') ||
        errorMessage.includes('AmazonConnectStreamsSite') ||
        errorMessage.includes('Attempted to initialize provider more than one time')
      ) {
        return;
      }
      
      // Llamar al console.error original
      originalError.apply(console, args);
    };

    // Verificar sesión al cargar
    const checkSession = async () => {
      try {
        await checkAndFixSession();
      } catch (error) {
        logger.warn('Error checking session:', error);
      }
    };

    checkSession();

    // Cleanup
    return () => {
      console.error = originalError;
    };
  }, []);

  const handleFixSession = async () => {
    setIsFixing(true);
    try {
      // Limpiar sesiones
      clearAuthSession();
      
      // Esperar un momento
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Verificar sesión nuevamente
      await checkAndFixSession();
      
      // Cerrar modal
      setShowErrorModal(false);
      
      // Recargar página para aplicar cambios
      window.location.reload();
    } catch (error) {
      logger.error('Error fixing session:', error);
    } finally {
      setIsFixing(false);
    }
  };

  const handleManualFix = () => {
    // Provide clear instructions to the user
    const instructions = `
To resolve this issue manually:

1. Open your browser developer tools (F12)
2. Go to the "Application" tab
3. Under "Storage", select "Local Storage"
4. Remove every entry that contains "supabase" or "sb-"
5. Refresh the page

Alternatively, you can:
- Clear your browser cache
- Make sure your system clock is correctly synchronized
    `;

    alert(instructions);
  };

  if (showErrorModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
              <span className="text-yellow-600 text-lg">⚠️</span>
            </div>
            <h3 className="text-lg font-semibold text-gray-900">
              Session Issue Detected
            </h3>
          </div>
          
          <p className="text-gray-600 mb-4">
            We detected a problem with the authentication session. This can be caused by:
          </p>
          
          <ul className="text-sm text-gray-600 mb-6 space-y-1">
            <li>• System clock out of sync</li>
            <li>• Old browser sessions that were not cleared</li>
            <li>• Timezone configuration issues</li>
          </ul>
          
          <div className="flex space-x-3">
            <button
              onClick={handleFixSession}
              disabled={isFixing}
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {isFixing ? 'Fixing…' : 'Fix Automatically'}
            </button>
            
            <button
              onClick={handleManualFix}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300"
            >
              Manual Instructions
            </button>
          </div>
          
          <button
            onClick={() => setShowErrorModal(false)}
            className="mt-3 w-full text-gray-500 hover:text-gray-700 text-sm"
          >
            Continue without fixing
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default SessionErrorHandler; 